import React, { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { yupResolver } from '@hookform/resolvers/yup';
import CloseIcon from '@mui/icons-material/Close';
import {
	Box,
	Dialog,
	TextField,
	Typography,
	FormControl,
	Autocomplete,
} from '@mui/material';

import { PrimaryButton } from 'src/components/common/buttons/PrimaryButton';
import { snackbar } from 'src/components/common/snackbar/Snackbar';
import {
	IStudyUnassignFormData,
	IStudyUnassignmentModalProps,
} from 'src/features/study_assignment/domain/types';
import { UnassignStudyAssignmentValidationSchema } from 'src/features/study_assignment/domain/validation';
import { STUDY_ASSIGNMENT_LOADING_KEY } from 'src/features/study_assignment/store';
import { updateStudyAssignment } from 'src/features/study_assignment/store/studyAssignmentThunks';
import { AppDispatch, RootState } from 'src/store';

export const StudyUnassignmentModal: React.FC<IStudyUnassignmentModalProps> = ({
	open,
	studyToUnassign,
	onClose,
	onSubmit,
}) => {
	const dispatch = useDispatch<AppDispatch>();
	const { siteSlug } = useParams<{ siteSlug: string }>();

	const isLoading = useSelector(
		(state: RootState) =>
			state.loading.loadingStates[STUDY_ASSIGNMENT_LOADING_KEY],
	);

	const {
		control,
		handleSubmit,
		reset,
		formState: { errors },
	} = useForm<IStudyUnassignFormData>({
		resolver: yupResolver(UnassignStudyAssignmentValidationSchema),
	});

	const handleClose = () => {
		if (isLoading) return;
		onClose();
	};

	const handleUnassign = async (data: IStudyUnassignFormData) => {
		if (!siteSlug || data.study_assignment_ids.length === 0) return;

		let successCount = 0;
		let errorOccurred = false;

		const promises = data.study_assignment_ids.map(
			(assignmentId) =>
				new Promise<void>((resolve) => {
					dispatch(
						updateStudyAssignment({
							request: { status: 'canceled' },
							siteSlug,
							studyAssignmentId: assignmentId,
							onSuccess: () => {
								successCount++;
								resolve();
							},
							onError: () => {
								errorOccurred = true;
								resolve();
							},
						}),
					);
				}),
		);

		await Promise.all(promises);

		onSubmit?.();

		const failureCount = data.study_assignment_ids.length - successCount;

		if (errorOccurred) {
			snackbar(
				`Successfully unassigned ${successCount} assignments. But, ${failureCount} user${failureCount === 1 ? '' : 's'} failed to unassign`,
				'warning',
			);
		} else if (successCount > 1) {
			snackbar(
				`${successCount} users unassigned successfully`,
				'success',
			);
		} else if (successCount === 1) {
			snackbar('User unassigned successfully', 'success');
		} else {
			snackbar('Unassignment failed for all selected users.', 'error');
		}
	};

	useEffect(() => {
		if (open && studyToUnassign?.assigned_user) {
			reset({
				study_assignment_ids: studyToUnassign.assigned_user
					.map((user) => user.study_assignment_id)
					.filter((id): id is number => id !== null),
			});
		}
	}, [open, studyToUnassign, reset]);

	if (!open) return null;

	return (
		<Dialog open={open} onClose={handleClose} fullWidth>
			<form onSubmit={handleSubmit(handleUnassign)}>
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'column',
						padding: '24px',
						gap: 3,
					}}
				>
					<Box
						sx={{
							display: 'flex',
							justifyContent: 'space-between',
						}}
					>
						<Typography variant='h6' fontWeight='bold'>
							Unassign Study
						</Typography>
						<CloseIcon
							onClick={handleClose}
							sx={{
								cursor: isLoading ? 'not-allowed' : 'pointer',
								color: isLoading ? 'gray' : 'black',
							}}
						/>
					</Box>

					<Box
						sx={{
							display: 'flex',
							flexDirection: 'column',
							gap: 1,
							color: 'red',
							fontStyle: 'italic',
						}}
					>
						<Controller
							name='study_assignment_ids'
							control={control}
							render={({ field: { onChange, value } }) => (
								<FormControl
									fullWidth
									error={!!errors.study_assignment_ids}
								>
									<Autocomplete
										multiple
										limitTags={2}
										options={
											studyToUnassign.assigned_user || []
										}
										getOptionLabel={(option) =>
											`${option.first_name} ${option.last_name} - ${option.email}`
										}
										value={
											studyToUnassign.assigned_user
												? studyToUnassign.assigned_user.filter(
														(user) =>
															user.study_assignment_id !==
																null &&
															value.includes(
																user.study_assignment_id,
															),
													)
												: []
										}
										onChange={(_, newValues) => {
											onChange(
												newValues.map(
													(user) =>
														user.study_assignment_id,
												),
											);
										}}
										ListboxProps={{
											style: {
												maxHeight: '300px',
												overflow: 'auto',
											},
										}}
										renderInput={(params) => (
											<TextField
												{...params}
												label='Assigned Users'
												helperText={
													errors.study_assignment_ids
														?.message
												}
												error={
													!!errors.study_assignment_ids
												}
											/>
										)}
									/>
								</FormControl>
							)}
						/>
						<Typography variant='caption'>
							Disclaimer: All selected user will be unassigned
							from the study
						</Typography>
					</Box>

					<PrimaryButton
						type='submit'
						isLoading={isLoading}
						sx={{ width: '100%', marginTop: '8px' }}
					>
						Unassign
					</PrimaryButton>
				</Box>
			</form>
		</Dialog>
	);
};
